import { AlertTriangle, Cloud, CloudOff, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/cn";
import { formatRelativeTime } from "@/lib/format";
import { useSyncStore } from "@/stores/syncStore";
import type { ConnectionStatus } from "@/sync/states";

/**
 * The connectivity indicator.
 *
 * Not in the supplied designs, and required by the brief: the interface must
 * visibly indicate Online, Offline, Synchronising, Synchronisation failed and the
 * last successful synchronisation time. It sits in the top bar because that is
 * the one region present on every screen — a cashier must never have to navigate
 * somewhere to discover they have been trading offline for an hour.
 *
 * Colour is never the only signal: each state carries a distinct icon and word.
 */
const PRESENTATION: Record<
  ConnectionStatus,
  { icon: typeof Cloud; classes: string; spin?: boolean }
> = {
  ONLINE: { icon: Cloud, classes: "bg-brand-50 text-brand-700 border-brand-100" },
  OFFLINE: { icon: CloudOff, classes: "bg-warn-50 text-warn-700 border-warn-500/20" },
  SYNCING: {
    icon: Loader2,
    classes: "bg-info-50 text-info-500 border-info-500/20",
    spin: true,
  },
  SYNC_FAILED: {
    icon: AlertTriangle,
    classes: "bg-danger-50 text-danger-700 border-danger-500/25",
  },
  CHECKING: { icon: RefreshCw, classes: "bg-canvas text-ink-500 border-line", spin: true },
};

export function SyncStatusChip({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const status = useSyncStore((s) => s.status);
  const pendingCount = useSyncStore((s) => s.pendingCount);
  const deadLetterCount = useSyncStore((s) => s.deadLetterCount);
  const lastSyncedAt = useSyncStore((s) => s.lastSyncedAt);

  const { icon: Icon, classes, spin } = PRESENTATION[status];

  const label = {
    ONLINE: t("sync.online"),
    OFFLINE: t("sync.offline"),
    SYNCING: t("sync.syncing"),
    SYNC_FAILED: t("sync.failed"),
    CHECKING: t("sync.checking"),
  }[status];

  // The count that matters differs by state: when something has failed, the
  // number a merchant needs is how many are stuck, not how many are queued.
  const badge = status === "SYNC_FAILED" ? deadLetterCount : pendingCount;

  const detail =
    status === "OFFLINE" || status === "SYNC_FAILED"
      ? t("sync.lastSynced", { when: formatRelativeTime(lastSyncedAt) })
      : null;

  return (
    <button
      type="button"
      onClick={() => navigate("/sync")}
      // The visible text is a short label; the accessible name spells out the
      // full state so it is not reduced to "Offline 3".
      aria-label={[label, badge > 0 ? t("sync.pendingCount", { count: badge }) : null, detail]
        .filter(Boolean)
        .join(". ")}
      title={detail ?? label}
      className={cn(
        "inline-flex h-touch items-center gap-2 rounded-pill border px-3 text-sm font-medium",
        "transition-colors hover:brightness-[0.98]",
        classes,
        className,
      )}
    >
      <Icon className={cn("size-4 shrink-0", spin && "animate-spin")} aria-hidden />
      <span className="whitespace-nowrap">{label}</span>

      {badge > 0 && (
        <span
          className="tabular grid h-5 min-w-5 place-items-center rounded-pill bg-current/15 px-1.5 text-xs font-semibold"
          aria-hidden
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}
